import React, { useState, useEffect } from "react";
import {
  Table,
  Typography,
  Upload,
  Button,
  Pagination,
  Input,
  Select,
  Modal,
} from "antd";
import Main from "../../components/layout/Main";
import TableImage from "../../assets/images/tabel-image.svg";
import ArrowLeftImg from "../../assets/images/arrow-left.svg";
import ArrowRightImg from "../../assets/images/arrow-right.svg";
import { useNavigate, useParams } from "react-router";
import { UploadOutlined } from "@ant-design/icons";
import { Link } from "lucide-react";
import { useForums } from "../../context/ForumsContext";

const { Option } = Select;
const { TextArea } = Input;

const ForumDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getForumById, getForums, createThread, isLoading } = useForums();

  const [threads, setThreads] = useState([]);
  const [forums, setForums] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });

  const [isForumModalVisible, setIsForumModalVisible] = useState(false);
  const [isTopicModalVisible, setIsTopicModalVisible] = useState(false);
  const [selectedForum, setSelectedForum] = useState(null);
  const [topicTitle, setTopicTitle] = useState("");
  const [topicDescription, setTopicDescription] = useState("");
  const [files, setFiles] = useState([]); // ✅ initialize as empty array

  const fetchThreads = async (page = 1, limit = 10) => {
    const res = await getForumById(id, { page, limit });
    if (res.success && res.data?.data) {
      const { data, total, page: currentPage, limit: pageSize } = res.data.data;

      const mapped = data.map((item) => ({
        key: item._id,
        title: item.threadName,
        pinned: false,
        image: TableImage,
        viewers: item.totalViews,
        comments: item.totalComments,
        user: {
          name: item.lastParticipation?.name || "Unknown",
          date: item.lastParticipation?.timeAgo || "-",
          profilePic: item.lastParticipation?.profilePic,
        },
      }));

      setThreads(mapped);
      setPagination({
        current: currentPage,
        total,
        pageSize,
      });
    }
  };

  useEffect(() => {
    fetchThreads(pagination.current, pagination.pageSize);
  }, [id]);

  const showForumModal = async () => {
    setIsForumModalVisible(true);
    try {
      const res = await getForums();
      if (res.success && res.data.data) {
        setForums(res.data.data);
      }
    } catch (error) {
      console.error("Failed to load forums:", error);
    }
  };

  const handleForumContinue = () => {
    if (selectedForum) {
      setIsForumModalVisible(false);
      setIsTopicModalVisible(true);
    }
  };

  const handleTopicSubmit = async () => {
    if (!selectedForum) {
      console.error("No forum selected");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("title", topicTitle);
      formData.append("content", topicDescription);
      formData.append("forumId", selectedForum);
      files.forEach((file) => formData.append("files", file));

      const res = await createThread(formData);
      if (res.success) {
        setIsTopicModalVisible(false);
        setTopicTitle("");
        setTopicDescription("");
        fetchThreads(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error("Failed to create thread:", error);
    }
  };

  const columns = [
    {
      title: "Subject",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={record.image}
            alt="icon"
            style={{
              width: 40,
              height: 40,
              borderRadius: 50,
              objectFit: "cover",
            }}
          />
          <span>
            {text}
            {record.pinned && (
              <span
                style={{ color: "#FF9355", marginLeft: 6, fontWeight: 700 }}
              >
                • Pinned
              </span>
            )}
          </span>
        </div>
      ),
    },
    { title: "No. of Viewers", dataIndex: "viewers", key: "viewers" },
    { title: "No. of Comments", dataIndex: "comments", key: "comments" },
    {
      title: "Last Participation",
      dataIndex: "user",
      key: "user",
      render: (user) => (
        <div className="participation-class">
          <h4>by {user.name}</h4>
          <h6>{user.date}</h6>
        </div>
      ),
    },
  ];

  const handlePageChange = (page) => {
    fetchThreads(page, pagination.pageSize);
  };
  return (
    <Main>
      <div className="section-main">
        <div>
          <div className="delivery-header topics-header mb-3">
            <h2>Up-Coming Events</h2>
            <Button className="fill-button-radius" onClick={showForumModal}>
              Start new topic
            </Button>
          </div>

          <Table
            loading={isLoading}
            columns={columns}
            dataSource={threads}
            pagination={false}
            bordered={false}
            rowClassName={() => "custom-row"}
            onRow={(record) => ({
              onClick: () => navigate(`/forums/finalroundnews/${record.key}`),
            })}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 24,
            }}
          >
            <Button
              className="arrow-paginateion"
              onClick={() => navigate(-1)}
              disabled={pagination.current === 1}
            >
              <img src={ArrowLeftImg} /> Previous
            </Button>

            <Pagination
              current={pagination.current}
              total={pagination.total}
              pageSize={pagination.pageSize}
              showSizeChanger={false}
              onChange={handlePageChange}
              style={{ color: "#fff" }}
            />

            <Button
              className="arrow-paginateion"
              onClick={() => handlePageChange(pagination.current + 1)}
              disabled={
                pagination.current * pagination.pageSize >= pagination.total
              }
            >
              Next <img src={ArrowRightImg} />
            </Button>
          </div>
        </div>
      </div>

      <Modal
        open={isForumModalVisible}
        title="Select Forum"
        footer={null}
        width={385}
        onCancel={() => setIsForumModalVisible(false)}
        className="custom-modal"
        centered
      >
        <Select
          placeholder="Select Forum"
          style={{ width: "100%", marginBottom: 20 }}
          onChange={(value) => setSelectedForum(value)}
          className="custom-select"
        >
          {forums.map((forum) => (
            <Option key={forum._id} value={forum._id}>
              {forum.title}
            </Option>
          ))}
        </Select>
        <Button
          className="Fill-button"
          block
          onClick={handleForumContinue}
          disabled={!selectedForum}
        >
          Continue
        </Button>
      </Modal>

      {/* Topic Modal */}
      <Modal
        open={isTopicModalVisible}
        title="Create topic"
        width={877}
        footer={null}
        onCancel={() => setIsTopicModalVisible(false)}
        className="custom-modal"
        centered
      >
        <Input
          className="input-full"
          placeholder="Title"
          style={{ marginBottom: 12 }}
          value={topicTitle}
          onChange={(e) => setTopicTitle(e.target.value.replace(/^\s+/, ""))}
        />
        <div className="upload-button-topics">
          <TextArea
            placeholder="Description"
            rows={4}
            style={{ marginBottom: 12 }}
            value={topicDescription}
            onChange={(e) =>
              setTopicDescription(e.target.value.replace(/^\s+/, ""))
            }
          />
          <div className="topics-upload-button">
            <Upload
              // multiple
              fileList={files}
              beforeUpload={(file) => {
                setFiles((prev) => [...prev, file]);
                return false;
              }}
              onRemove={(file) => {
                setFiles((prev) => prev.filter((f) => f.uid !== file.uid));
              }}
            >
              <div className="topics-upload-box">
                <Link />
                <div className="custom-upload-menu">
                  <span>
                    Drag files here to attach, or{" "}
                    <Button>Choose files...</Button>
                  </span>
                  <p>Max total size: 500 kB</p>
                </div>
              </div>
            </Upload>
          </div>
        </div>
        <p className="note-text">
          Your topic will need to be approved by a moderator.
        </p>
        <Button className="Fill-button" block onClick={handleTopicSubmit}>
          Submit topic
        </Button>
      </Modal>
    </Main>
  );
};

export default ForumDetailPage;
